import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, useUser, UserButton } from '@clerk/clerk-react';
import {
  Building2,
  Phone,
  MapPin,
  Globe,
  FileText,
  Upload,
  Save,
  Loader2,
  Home,
  CreditCard,
  Shield,
  AlertCircle
} from 'lucide-react';
import { getUserSettings, updateUserSettings, uploadLogo } from '../utils/profileClient';
import { createPortalSession } from '../utils/subscriptionClient';
import { useSubscription } from '../hooks/useSubscription';

const Profile = () => {
  const { user } = useUser();
  const { getToken } = useAuth();
  const { subscriptionStatus, loading: subLoading } = useSubscription();

  const [settings, setSettings] = useState({
    company_name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    license_number: '',
    website: '',
    default_terms: '',
    logo_url: '',
    default_labor_rate: '',
    pdf_settings: {
      show_logo: true,
      show_license: true,
      show_company_info: true,
      color_scheme: '#1e3a5f'
    }
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Connected Suppliers state
  const [suppliers, setSuppliers] = useState([]);
  const [shareCode, setShareCode] = useState('');
  const [supplierMsg, setSupplierMsg] = useState({ type: '', text: '' });
  const [connecting, setConnecting] = useState(false);

  const API = process.env.REACT_APP_API_URL || 'https://tool.estimateassistant.com';

  const loadSuppliers = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v2/user/supplier/connected`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setSuppliers(data.suppliers || []);
    } catch (err) {
      console.warn('Could not load suppliers:', err.message);
    }
  };

  const handleConnect = async () => {
    if (!shareCode.trim()) return;
    setConnecting(true);
    setSupplierMsg({ type: '', text: '' });
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v2/user/supplier/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ shareCode: shareCode.trim() })
      });
      const data = await res.json();
      if (data.success) {
        setSupplierMsg({ type: 'success', text: `Connected to ${data.supplier.name}` });
        setShareCode('');
        loadSuppliers();
      } else {
        setSupplierMsg({ type: 'error', text: data.error || 'Connection failed.' });
      }
    } catch (err) {
      setSupplierMsg({ type: 'error', text: 'Connection failed.' });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async (supplierId) => {
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v2/user/supplier/disconnect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ supplierId })
      });
      const data = await res.json();
      if (data.success) loadSuppliers();
    } catch (err) {
      console.warn('Disconnect failed:', err.message);
    }
  };

  useEffect(() => {
    loadSettings();
    loadSuppliers();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await getUserSettings(getToken);

      // Merge with defaults
      setSettings({
        company_name: data.company_name || '',
        phone: data.phone || '',
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        zip: data.zip || '',
        license_number: data.license_number || '',
        website: data.website || '',
        default_terms: data.default_terms || '',
        logo_url: data.logo_url || '',
        default_labor_rate: data.default_labor_rate || '',
        pdf_settings: {
          show_logo: data.pdf_settings?.show_logo ?? true,
          show_license: data.pdf_settings?.show_license ?? true,
          show_company_info: data.pdf_settings?.show_company_info ?? true,
          color_scheme: data.pdf_settings?.color_scheme || '#1e3a5f'
        }
      });
    } catch (error) {
      console.error('Error loading settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      await updateUserSettings(getToken, {
        ...settings,
        pdf_settings: JSON.stringify(settings.pdf_settings)
      });

      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingLogo(true);
      setMessage({ type: '', text: '' });

      const { logoUrl } = await uploadLogo(getToken, file);
      setSettings({ ...settings, logo_url: logoUrl });
      setMessage({ type: 'success', text: 'Logo uploaded successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error uploading logo:', error);
      setMessage({ type: 'error', text: 'Failed to upload logo' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      await createPortalSession(user);
    } catch (error) {
      console.error('Error opening customer portal:', error);
      if (error.message.includes('No subscription found')) {
        if (window.confirm('You don\'t have an active subscription. Would you like to view pricing?')) {
          window.location.href = '/pricing';
        }
      } else {
        alert('Failed to open subscription management. Please try again.');
      }
    }
  };

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const handlePdfSettingChange = (field, value) => {
    setSettings({
      ...settings,
      pdf_settings: {
        ...settings.pdf_settings,
        [field]: value
      }
    });
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader2 size={48} color="#1e3a5f" className="animate-spin" />
          <p style={{ marginTop: '1rem', color: '#64748b' }}>Loading profile...</p>
        </div>
      </div>
    );
  }

  const isSubscribed = subscriptionStatus?.subscribed === true || subscriptionStatus?.status === 'active';

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
    }}>
      {/* Header */}
      <header style={{
        backgroundColor: '#1e3a5f',
        color: 'white',
        padding: '1.5rem 2rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <img
              src="/Housecalclogo.png"
              alt="Estimate Assistant"
              style={{
                height: '50px',
                width: 'auto'
              }}
            />
            <div>
              <h1 style={{
                margin: 0,
                fontSize: '1.75rem',
                fontWeight: '600',
                letterSpacing: '-0.025em'
              }}>
                Company Profile & Settings
              </h1>
              <p style={{
                margin: '0.25rem 0 0 0',
                opacity: 0.9,
                fontSize: '0.95rem'
              }}>
                Manage your company information and PDF preferences
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Link
              to="/dashboard"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '0.5rem 1rem',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                textDecoration: 'none',
                transition: 'all 0.2s'
              }}
            >
              <Home size={18} />
              Dashboard
            </Link>
            <UserButton afterSignOutUrl="/" />
          </div>
        </div>
      </header>

      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '2rem'
      }}>
        {/* Message */}
        {message.text && (
          <div style={{
            backgroundColor: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
            border: `2px solid ${message.type === 'success' ? '#86efac' : '#fecaca'}`,
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '2rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            color: message.type === 'success' ? '#15803d' : '#991b1b'
          }}>
            {message.type === 'success' ? '✓' : <AlertCircle size={20} />}
            <span style={{ fontWeight: '500' }}>{message.text}</span>
          </div>
        )}

        {/* Subscription Status */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <CreditCard size={24} color="#1e3a5f" />
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', color: '#1e293b' }}>
                  Subscription Status
                </h3>
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#64748b' }}>
                  {subLoading ? 'Checking...' : isSubscribed ?
                    `Active (${subscriptionStatus?.status})` :
                    'Free Plan'}
                </p>
              </div>
            </div>
            <button
              onClick={handleManageSubscription}
              style={{
                backgroundColor: '#1e3a5f',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s'
              }}
            >
              <Shield size={18} />
              {isSubscribed ? 'Manage Subscription' : 'Upgrade to Pro'}
            </button>
          </div>
        </div>

        <form onSubmit={handleSave}>
          <div style={{ display: 'grid', gap: '2rem' }}>
            {/* Company Information */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '2rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <Building2 size={24} color="#1e3a5f" />
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1e293b' }}>
                  Company Information
                </h2>
              </div>

              <div style={{ display: 'grid', gap: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={settings.company_name}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      boxSizing: 'border-box'
                    }}
                    placeholder="ABC Construction Co."
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={settings.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        boxSizing: 'border-box'
                      }}
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                      License Number
                    </label>
                    <input
                      type="text"
                      value={settings.license_number}
                      onChange={(e) => handleChange('license_number', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        boxSizing: 'border-box'
                      }}
                      placeholder="LIC-123456"
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                    Website
                  </label>
                  <input
                    type="url"
                    value={settings.website}
                    onChange={(e) => handleChange('website', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      boxSizing: 'border-box'
                    }}
                    placeholder="https://www.example.com"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                    Street Address
                  </label>
                  <input
                    type="text"
                    value={settings.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      boxSizing: 'border-box'
                    }}
                    placeholder="123 Main St"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                      City
                    </label>
                    <input
                      type="text"
                      value={settings.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        boxSizing: 'border-box'
                      }}
                      placeholder="San Francisco"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                      State
                    </label>
                    <input
                      type="text"
                      value={settings.state}
                      onChange={(e) => handleChange('state', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        boxSizing: 'border-box'
                      }}
                      placeholder="CA"
                      maxLength="2"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                      ZIP Code
                    </label>
                    <input
                      type="text"
                      value={settings.zip}
                      onChange={(e) => handleChange('zip', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        boxSizing: 'border-box'
                      }}
                      placeholder="94102"
                    />
                  </div>
                </div>
              </div>
            </div>
			
{/* Connected Suppliers */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '2rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <FileText size={24} color="#1e3a5f" />
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1e293b' }}>
                  Connected Suppliers
                </h2>
              </div>
              <p style={{ margin: '0 0 1.5rem 0', fontSize: '0.875rem', color: '#64748b' }}>
                Enter a supplier's code to use their pricing in your estimates.
              </p>

              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                <input
                  type="text"
                  value={shareCode}
                  onChange={(e) => setShareCode(e.target.value)}
                  placeholder="Enter supplier code"
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
                <button
                  type="button"
                  onClick={handleConnect}
                  disabled={connecting || !shareCode.trim()}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#1e3a5f',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '500',
                    cursor: connecting ? 'default' : 'pointer',
                    opacity: (connecting || !shareCode.trim()) ? 0.6 : 1
                  }}
                >
                  {connecting ? 'Connecting…' : 'Connect'}
                </button>
              </div>

              {supplierMsg.text && (
                <p style={{
                  margin: '0 0 1rem 0',
                  fontSize: '0.875rem',
                  color: supplierMsg.type === 'success' ? '#065f46' : '#b91c1c'
                }}>
                  {supplierMsg.text}
                </p>
              )}

              {suppliers.length === 0 ? (
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#94a3b8', fontStyle: 'italic' }}>
                  No suppliers connected yet.
                </p>
              ) : (
                <div>
                  {suppliers.map((s) => (
                    <div key={s.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.75rem 1rem',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      marginBottom: '0.5rem'
                    }}>
                      <div>
                        <span style={{ fontWeight: '500', color: '#1e293b' }}>{s.name}</span>
                        <span style={{ marginLeft: '0.75rem', fontSize: '0.8rem', color: '#94a3b8' }}>
                          {s.item_count} items
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDisconnect(s.id)}
                        style={{
                          padding: '0.4rem 0.9rem',
                          backgroundColor: 'white',
                          color: '#b91c1c',
                          border: '1px solid #fecaca',
                          borderRadius: '6px',
                          fontSize: '0.85rem',
                          cursor: 'pointer'
                        }}
                      >
                        Disconnect
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Estimating Defaults */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '2rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <FileText size={24} color="#1e3a5f" />
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1e293b' }}>
                  Estimating Defaults
                </h2>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                  Default Labor Rate ($/hr)
                </label>
                <input
                  type="number"
                  value={settings.default_labor_rate}
                  onChange={(e) => handleChange('default_labor_rate', e.target.value)}
                  min="0"
                  step="1"
                  style={{
                    width: '200px',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                  placeholder="e.g. 75"
                />
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#64748b' }}>
                  Your hourly labor rate. This will auto-fill when labor items are added to estimates.
                </p>
              </div>
            </div>

            {/* Company Logo */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '2rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <Upload size={24} color="#1e3a5f" />
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1e293b' }}>
                  Company Logo
                </h2>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
                {settings.logo_url && (
                  <img
                    src={settings.logo_url}
                    alt="Company Logo"
                    style={{
                      width: '120px',
                      height: '120px',
                      objectFit: 'contain',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '0.5rem'
                    }}
                  />
                )}

                <div style={{ flex: 1 }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    disabled={uploadingLogo}
                    style={{ display: 'none' }}
                    id="logo-upload"
                  />
                  <label
                    htmlFor="logo-upload"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      backgroundColor: uploadingLogo ? '#9ca3af' : '#1e3a5f',
                      color: 'white',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: uploadingLogo ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '0.95rem'
                    }}
                  >
                    {uploadingLogo ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload size={18} />
                        Upload Logo
                      </>
                    )}
                  </label>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#64748b' }}>
                    PNG, JPG or SVG. Max 2MB. Will appear on your estimates.
                  </p>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#f59e0b' }}>
                    ⚠️ Note: Railway has ephemeral storage. Consider AWS S3 or Cloudinary for production.
                  </p>
                </div>
              </div>
            </div>

            {/* PDF Settings */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '2rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <FileText size={24} color="#1e3a5f" />
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1e293b' }}>
                  PDF Estimate Settings
                </h2>
              </div>

              <div style={{ display: 'grid', gap: '1.5rem' }}>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={settings.pdf_settings.show_logo}
                      onChange={(e) => handlePdfSettingChange('show_logo', e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: '500', color: '#374151' }}>Show company logo on estimates</span>
                  </label>
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={settings.pdf_settings.show_license}
                      onChange={(e) => handlePdfSettingChange('show_license', e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: '500', color: '#374151' }}>Show license number on estimates</span>
                  </label>
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={settings.pdf_settings.show_company_info}
                      onChange={(e) => handlePdfSettingChange('show_company_info', e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: '500', color: '#374151' }}>Show company address and contact info</span>
                  </label>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                    PDF Color Scheme
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <input
                      type="color"
                      value={settings.pdf_settings.color_scheme}
                      onChange={(e) => handlePdfSettingChange('color_scheme', e.target.value)}
                      style={{
                        width: '60px',
                        height: '40px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{ color: '#64748b', fontSize: '0.875rem' }}>
                      Choose your brand color for estimate headers and accents
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Default Terms & Conditions */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '2rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <FileText size={24} color="#1e3a5f" />
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1e293b' }}>
                  Default Terms & Conditions
                </h2>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                  Terms & Conditions Text
                </label>
                <textarea
                  value={settings.default_terms}
                  onChange={(e) => handleChange('default_terms', e.target.value)}
                  rows={6}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Enter your default terms and conditions that will appear on all estimates..."
                />
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#64748b' }}>
                  This text will be automatically included at the bottom of all your estimates.
                </p>
              </div>
            </div>

            {/* Save Button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <Link
                to="/dashboard"
                style={{
                  backgroundColor: 'white',
                  color: '#1e3a5f',
                  border: '2px solid #1e3a5f',
                  borderRadius: '8px',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  display: 'inline-block'
                }}
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={saving}
                style={{
                  backgroundColor: saving ? '#9ca3af' : '#1e3a5f',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s'
                }}
              >
                {saving ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
};

export default Profile;
